"use client";

import { SafeHydrate } from "@/SafeHydrate";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Checkbox } from "@/components/ui/checkbox";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { Calendar as CalendarIcon, Loader as Loader2 } from 'lucide-react';
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { useState } from "react";
export default function LetSCreateMagic() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: gridRef,
    isVisible: gridVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="832" id="lets-create-magic" className="relative bg-background py-24 md:py-32 lg:py-40 overflow-hidden light">
      {}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-40" style={{
      backgroundImage: "radial-gradient(ellipse at 50% 0%, hsl(25 40% 65% / 0.15), transparent 60%)"
    }} />

      <div className="relative mx-auto max-w-7xl px-6 lg:px-8">
        {}
        <div ref={headerRef} className={`mx-auto max-w-[720px] text-center transition-all duration-[1200ms] ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <span className="font-default text-xs uppercase tracking-[0.3em] text-muted-foreground">
            Inquiry
          </span>
          <h2 className="font-serif italic text-foreground mt-6 leading-[1.05]" style={{
          fontSize: "clamp(3rem, 6vw, 5rem)"
        }}>
            Let's create
            <br />
            magic.
          </h2>
          <p className="font-default mt-8 text-[18px] leading-relaxed text-muted-foreground">
            Whether you are preparing for your wedding day or seeking a transformative editorial look, I am here to bring your vision to life. Share your details below, and let's begin the artistry.
          </p>
        </div>

        {}
        <div ref={gridRef} className={`mt-20 lg:mt-[80px] grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 transition-all duration-[1200ms] ease-out delay-200 ${gridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {}
          <aside className="lg:col-span-5 order-1">
            <div className="lg:sticky lg:top-24">
              <div className="bg-card border border-border p-8 md:p-10 rounded-lg">
                <span className="font-default text-[11px] uppercase tracking-[0.25em] text-muted-foreground">
                  A note from Joanne
                </span>
                <p className="font-serif italic text-[18px] leading-[1.7] text-foreground/90 mt-6">
                  I personally review every inquiry to ensure I can provide the bespoke attention your event deserves. Expect a response within 48 hours to discuss your beauty vision.
                </p>

                <Separator className="my-8 bg-border" />

                <div className="space-y-5">
                  <div>
                    <span className="font-default text-[10px] uppercase tracking-[0.25em] text-muted-foreground block mb-2">
                      Direct
                    </span>
                    <p className="font-default text-sm text-foreground">
                      hello@joanneglow.com
                    </p>
                  </div>
                  <div>
                    <span className="font-default text-[10px] uppercase tracking-[0.25em] text-muted-foreground block mb-2">
                      Studio Inquiries
                    </span>
                    <p className="font-default text-sm text-foreground">
                      (555) 123-4567
                    </p>
                  </div>
                  <div>
                    <span className="font-default text-[10px] uppercase tracking-[0.25em] text-muted-foreground block mb-2">
                      Location
                    </span>
                    <p className="font-default text-sm text-foreground/80 leading-relaxed">
                      The Atelier
                      <br />
                      Beauty District, BD 90210
                    </p>
                  </div>
                </div>

                <div className="mt-10 pt-6 border-t border-border">
                  <p className="font-serif italic text-2xl text-primary">
                    — Joanne
                  </p>
                </div>
              </div>
            </div>
          </aside>

          {}
          <div className="lg:col-span-7 order-2">
            <FormProvider formKey="magic_form_get_in_touch" formId="magic_form_get_in_touch" sectionName="lets-create-magic" defaultValues={{
            name: "",
            email: "",
            phone: "",
            session_type: "",
            preferred_date: "",
            experience: "",
            message: "",
            moon_letters: false
          }} validationRules={{
            name: {
              required: true,
              minLength: 2
            },
            email: {
              required: true,
              pattern: "email"
            },
            session_type: {
              required: true
            }
          }} submitText="Send Your Inquiry" successMessage="Your vision has been received. I look forward to creating with you. — Joanne." errorMessage="Something went wrong. Please try again or email me directly.">
              <HealingForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}
function HealingForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  const [calendarOpen, setCalendarOpen] = useState(false);
  if (isSubmitted && !submitError) {
    return <div className="min-h-[400px] flex flex-col items-center justify-center text-center px-4">
        <div className="relative mb-10">
          <div className="size-24 rounded-lg border border-primary/40 flex items-center justify-center bg-primary/5 animate-pulse">
            <div className="size-12 rounded-lg bg-primary/30" />
          </div>
        </div>
        <p className="font-serif italic text-foreground leading-tight max-w-md" style={{
        fontSize: "clamp(1.75rem, 3vw, 2.5rem)"
      }}>
          {successMessage}
        </p>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      {}
      <FormField control={control} name="name" rules={{
      required: "Your name is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default text-[11px] uppercase tracking-[0.25em] text-muted-foreground">
              Full Name
            </FormLabel>
            <FormControl>
              <Input placeholder="Jane Doe" {...field} className="border-0 border-b border-border bg-transparent px-0 h-11 focus-visible:ring-0 focus-visible:border-primary transition-colors text-foreground placeholder:text-muted-foreground/60" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {}
      <FormField control={control} name="email" rules={{
      required: "An email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Please enter a valid email"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default text-[11px] uppercase tracking-[0.25em] text-muted-foreground">
              Email Address
            </FormLabel>
            <FormControl>
              <Input type="email" placeholder="jane@example.com" {...field} className="border-0 border-b border-border bg-transparent px-0 h-11 focus-visible:ring-0 focus-visible:border-primary transition-colors text-foreground placeholder:text-muted-foreground/60" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {}
      <FormField control={control} name="phone" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default text-[11px] uppercase tracking-[0.25em] text-muted-foreground">
              Phone Number <span className="normal-case tracking-normal text-muted-foreground/60">(optional)</span>
            </FormLabel>
            <FormControl>
              <Input type="tel" placeholder="+1 (555) 123-4567" {...field} className="border-0 border-b border-border bg-transparent px-0 h-11 focus-visible:ring-0 focus-visible:border-primary transition-colors text-foreground placeholder:text-muted-foreground/60" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {}
      <FormField control={control} name="session_type" rules={{
      required: "Please select a service"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default text-[11px] uppercase tracking-[0.25em] text-muted-foreground">
              Service Requested
            </FormLabel>
            <Select onValueChange={field.onChange} value={field.value}>
              <FormControl>
                <SelectTrigger className="border-0 border-b border-border bg-transparent px-0 h-11 focus-visible:ring-0 focus-visible:border-primary transition-colors text-foreground data-[placeholder]:text-muted-foreground/60 w-full">
                  <SelectValue placeholder="Select a service" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                <SelectItem value="Bridal Makeup">Bridal Makeup</SelectItem>
                <SelectItem value="Editorial Styling">Editorial Styling</SelectItem>
                <SelectItem value="Special Event">Special Event</SelectItem>
                <SelectItem value="Makeup Lesson">Makeup Lesson</SelectItem>
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>} />

      {}
      <FormField control={control} name="preferred_date" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default text-[11px] uppercase tracking-[0.25em] text-muted-foreground">
              Event Date <span className="normal-case tracking-normal text-muted-foreground/60">(if known)</span>
            </FormLabel>
            <FormControl>
              <div className="relative">
                <Button type="button" variant="ghost" onClick={() => setCalendarOpen(!calendarOpen)} className="w-full border-0 border-b border-border bg-transparent px-0 h-11 text-left text-foreground hover:bg-transparent hover:border-primary transition-colors flex items-center justify-between focus:outline-none focus:border-primary">
                  <span className={field.value ? "text-foreground" : "text-muted-foreground/60"}>
                    {field.value || "Select a date"}
                  </span>
                  <SafeHydrate><CalendarIcon className="size-4 text-muted-foreground" /></SafeHydrate>
                </Button>
                {calendarOpen && <div className="absolute z-20 mt-2 bg-popover border border-border rounded-lg shadow-lg">
                    <SafeHydrate><Calendar mode="single" selected={field.value ? new Date(field.value) : undefined} onSelect={date => {
                if (date) {
                  field.onChange(date.toISOString().split("T")[0]);
                }
                setCalendarOpen(false);
              }} /></SafeHydrate>
                  </div>}
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {}
      <FormField control={control} name="experience" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default text-[11px] uppercase tracking-[0.25em] text-muted-foreground">
              How did you hear about us?
            </FormLabel>
            <Select onValueChange={field.onChange} value={field.value}>
              <FormControl>
                <SelectTrigger className="border-0 border-b border-border bg-transparent px-0 h-11 focus-visible:ring-0 focus-visible:border-primary transition-colors text-foreground data-[placeholder]:text-muted-foreground/60 w-full">
                  <SelectValue placeholder="Choose one" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                <SelectItem value="Instagram">Instagram</SelectItem>
                <SelectItem value="Referral">Referral</SelectItem>
                <SelectItem value="Wedding Planner">Wedding Planner</SelectItem>
                <SelectItem value="Other">Other</SelectItem>
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>} />

      {}
      <FormField control={control} name="message" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default text-[11px] uppercase tracking-[0.25em] text-muted-foreground">
              Tell me about your vision
            </FormLabel>
            <FormControl>
              <Textarea rows={5} placeholder="Share your event details, style preferences, or any specific questions." {...field} className="border-0 border-b border-border bg-transparent px-0 py-2 focus-visible:ring-0 focus-visible:border-primary transition-colors text-foreground placeholder:text-muted-foreground/60 resize-none min-h-[140px] font-default" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {}
      <FormField control={control} name="moon_letters" render={({
      field
    }) => <FormItem>
            <div className="flex items-start gap-3 pt-2">
              <FormControl>
                <Checkbox checked={field.value} onCheckedChange={field.onChange} className="mt-1 border-border data-[state=checked]:bg-primary data-[state=checked]:border-primary" />
              </FormControl>
              <FormLabel className="font-default text-sm text-muted-foreground leading-relaxed font-normal cursor-pointer">
                Keep me updated with beauty tips and studio news.
              </FormLabel>
            </div>
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/30 rounded-lg">
          <p className="text-destructive text-sm font-default">{submitError}</p>
        </div>}

      {}
      <div className="pt-8 space-y-6">
        <p className="font-default text-xs text-muted-foreground italic">
          Your information is kept private and secure.
        </p>
        <Button type="submit" disabled={isSubmitting} size="lg" className="w-full md:w-3/5 h-14 bg-primary text-primary-foreground hover:bg-primary/90 font-default text-xs uppercase tracking-[0.25em] shadow-md transition-all">
          {isSubmitting ? <>
              <Loader2 className="animate-spin mr-2 size-4" />
              Sending
            </> : "Send Your Inquiry"}
        </Button>
      </div>
    </Form>;
}